import type React from 'react';
import type {ContainerQueryObject, ContainerQueryResult} from '@Hooks/useContainerQuery';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import type {PosterPageData} from '@Libraries/poster-library';
import type {TabKey} from '@Components/tabs';
import {MOBILE_SCREEN_BREAKPOINT} from '@Utils/dom.util';

export const DESIGN_SELECTOR_DESIGNS_TAB_KEY = 'design-selector-designs';

export interface OnPosterCreatedCallbackParams {
  newlyCreatedPosterHashedId: string;
  currentIndex?: number;
  totalPosterCreationsCount?: number;
  /** The design was composed on one timeline (a video selection), so the editor should open with the timeline shown. */
  shouldOpenTimeline?: boolean;
}

export interface CreateDesignFromMediaModalOpts {
  allowMultipagePosterCreation: boolean;
  /** A multi-selection containing a video becomes a single sequential video page instead of a multipage design. */
  createSequentialDesignForVideoSelections?: boolean;
  maxSelectionNumber: number;
  shouldUseFastPendingImageUploadFlow?: boolean;
  onTriggered?: () => void;
  onPosterCreated: (params: OnPosterCreatedCallbackParams) => void;
  onAllPostersCreated?: (newlyCreatedPosterHashedIds: string[], failedUploads: File[]) => void;
  onPosterCreationsStarted?: () => void;
  onErrorDuringUpload?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
  idFolder?: string;
  footerPrimaryCtaText?: string;
  hideFooterSecondaryActions?: boolean;
}

export enum DesignSelectorTab {
  MY_DESIGNS = 'design-selector-my-designs',
  SHARED_WITH_ME = 'design-selector-shared-with-me',
  GALLERY_TEMPLATES = 'design-selector-gallery-templates',
  BRAND_TEMPLATES = 'design-selector-brand-templates',
}

export enum DesignSelectorFrameVariant {
  DEFAULT = 'default',
  SOCIAL_MEDIA_WIZARD = 'social-media-wizard',
}

/** Consumed by `assets/css/views/design-selector-dialog.scss` — must stay a plain, non-hashed class. */
export const DESIGN_SELECTOR_ELEMENT_DESKTOP_CLASS = 'design-selector-element-desktop';
export const DESIGN_SELECTOR_ELEMENT_WIZARD_CLASS = 'design-selector-element-wizard';

export interface FolderGridBand {
  containerQueryKey: string;
  scopeClass: string;
  columnCount: number;
  /** Skeleton brick gutter. Must stay equal to the band's SCSS gutter so loader and grid cannot drift. */
  gutterClass: string;
  minContainerWidth: number;
  maxContainerWidth?: number;
}

/** A band's min-width floor is columns * 176px + gutters. */
export const FOLDER_GRID_BANDS: FolderGridBand[] = [
  {containerQueryKey: 'folders8', scopeClass: 'design-selector-element-folders-8', columnCount: 8, gutterClass: 'spacing-m-r-6', minContainerWidth: 1600},
  {containerQueryKey: 'folders7', scopeClass: 'design-selector-element-folders-7', columnCount: 7, gutterClass: 'spacing-m-r-6', minContainerWidth: 1400, maxContainerWidth: 1599},
  {containerQueryKey: 'folders6', scopeClass: 'design-selector-element-folders-6', columnCount: 6, gutterClass: 'spacing-m-r-6', minContainerWidth: 1200, maxContainerWidth: 1399},
  {containerQueryKey: 'folders5', scopeClass: 'design-selector-element-folders-5', columnCount: 5, gutterClass: 'spacing-m-r-6', minContainerWidth: 1000, maxContainerWidth: 1199},
  {containerQueryKey: 'folders4', scopeClass: 'design-selector-element-folders-4', columnCount: 4, gutterClass: 'spacing-m-r-6', minContainerWidth: 800, maxContainerWidth: 999},
  {containerQueryKey: 'folders3', scopeClass: 'design-selector-element-folders-3', columnCount: 3, gutterClass: 'spacing-m-r-5', minContainerWidth: 600, maxContainerWidth: 799},
  {containerQueryKey: 'folders2', scopeClass: 'design-selector-element-folders-2', columnCount: 2, gutterClass: 'spacing-m-r-4', minContainerWidth: 320, maxContainerWidth: 599},
];

export const FOLDER_GRID_COLUMN_COUNT_BELOW_NARROWEST_BAND = 1;

/** True once the container reports a non-zero width, so "no band matched because the container is very narrow" can be told apart from "the container hasn't been measured yet". */
export const FOLDER_GRID_CONTAINER_MEASURED_QUERY_KEY = 'measured';

export const folderGridContainerQuery: ContainerQueryObject = {
  [FOLDER_GRID_CONTAINER_MEASURED_QUERY_KEY]: {minWidth: 1},
  ...Object.fromEntries(
    FOLDER_GRID_BANDS.map((band) => {
      return [
        band.containerQueryKey,
        band.maxContainerWidth === undefined ? {minWidth: band.minContainerWidth} : {minWidth: band.minContainerWidth, maxWidth: band.maxContainerWidth},
      ];
    })
  ),
};

export function getActiveFolderGridBand(folderGridParams: ContainerQueryResult): FolderGridBand | null {
  return (
    FOLDER_GRID_BANDS.find((band) => {
      return folderGridParams[band.containerQueryKey];
    }) ?? null
  );
}

export function getDesignSelectorTopTabKey(designSelectorTab: DesignSelectorTab): TabKey {
  return designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES ? DesignSelectorTab.GALLERY_TEMPLATES : DESIGN_SELECTOR_DESIGNS_TAB_KEY;
}

export enum LimitExceededAction {
  /** Roll back the click; selection stays at the current size. The default behavior when no callback is supplied. */
  REJECT = 'reject',
  /** Accept the click even though it exceeds the limit. Use this if the caller wants to show their own warning UI but keep the user's selection. */
  ALLOW = 'allow',
  /** Auto-deselect the oldest selections to make room for the new click. */
  EVICT_OLDEST = 'evict-oldest',
}

/**
 * Info passed to `onLimitExceeded` when a click would push the selection past `maxSelection`.
 *
 * Generic over the selection item type so the same shape serves both the React `<DesignSelector>`
 * prop (defaults to `DesignSelection` — the `{posterHashedId, pageHashedId}` form React works
 * with) and the vanilla `window.PMW.designSelector` opts (uses `PosterPageData` — the
 * `{posterId, pageId, previewUrl, ...}` form the coordinator yields).
 */
export interface LimitExceededInfo<TItem = DesignSelection> {
  /** The selection state before this click was processed. */
  currentSelection: TItem[];
  /** What the selection would become if the click were accepted as-is. */
  tentativeSelection: TItem[];
  /** The items the user just clicked to add (the click delta). */
  newlyAdded: TItem[];
  /** The configured max. */
  maxSelection: number;
  /**
   * True when the click would add more than one item at once — i.e. the multipage popup's "Select All" button,
   * not an individual page click. Useful for callers who want EVICT_OLDEST on single clicks but REJECT on bulk
   * (since EVICT_OLDEST on a bulk add produces a confusing "X of Y selected" partial result).
   */
  isBulkAdd: boolean;
}

export interface DesignSelectorProps {
  onDesignsSelection?: (designsSelectionInformation: DesignSelection[]) => void;
  initialDesignSelections?: DesignSelection[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  onGalleryTemplateSelectionCallback?: (designs: DesignSelection[]) => void;
  onTabSelected?: (newTabSelection: DesignSelectorTab) => void;
  designSelectorTab?: DesignSelectorTab;
  startDesignFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts;
  onMyDesignsCreateWithAiClick?: () => void;
  onGalleryCreateWithAiClick?: () => void;
  isLoading?: boolean;
  designGalleryStartingURI?: GalleryPageURI;
  hideTabContainer?: boolean;
  frameVariant?: DesignSelectorFrameVariant;
  desktopClasses?: string;
  mobileClasses?: string;
  designGalleryHeight?: string;
  maxSelection?: number;
  onLimitExceeded?: (info: LimitExceededInfo) => LimitExceededAction;
  scrollContainerEl?: HTMLElement | Window | null;
  /** Hands back one row per design rather than one per page, and skips the page picker for multi-page designs. */
  doSelectPosterOnly?: boolean;
  /** The Brand templates tab's picks, kept apart from the My designs grid's own selection. */
  brandTemplateSelections?: DesignSelection[];
  onBrandTemplatesSelection?: (designs: DesignSelection[]) => void;
  /** The kit a brand-kit picker adds to: the Brand templates tab leaves it out, since its own templates are already in it. */
  brandTemplatesExcludedBrandId?: string;
  selectedDesignCount?: number;
  onDeselectAll?: () => void;
}

export interface DesignSelectorPageLimitBanner {
  threshold: number;
  desktopMessageKey: string;
  mobileMessageKey: string;
}

/**
 * A stable empty preselection. The selector re-applies whatever it is handed as the initial selection
 * whenever that array's identity changes, so a fresh `[]` on every render would wipe each pick.
 */
export const NO_PRESELECTED_DESIGNS: DesignSelection[] = [];

/**
 * Opts for `window.PMW.designSelector.open` (modal mode).
 */
export interface DesignSelectorOpts {
  title?: string;
  addSelectedCtaText?: string;
  preselectedDesigns?: (PosterPageData | DesignSelection)[];
  isMultiSelectionAllowed?: boolean;
  doSelectPosterOnly?: boolean;
  displayOnlyUnsharedFromTeamSpace?: boolean;
  maxSelection?: number;
  onLimitExceeded?: (info: LimitExceededInfo<PosterPageData>) => LimitExceededAction;
  addCheckmarkOnSelection?: boolean;
  pageLimitBanner?: DesignSelectorPageLimitBanner;
  selectedNameClass?: string;
  onDialogLoaded?: () => void;
  onSelectionChange?: (selection: PosterPageData[]) => void;
  onAddSelected?: (selection: PosterPageData[]) => void;
  startFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts;
  onCreateWithAiClick?: () => void;
  unhideSelectAllButton?: boolean;
}

/**
 * Opts for `window.PMW.designSelector.openEmbedded` (embedded mode).
 */
export interface DesignSelectorEmbeddedOpts extends Omit<DesignSelectorOpts, 'title'> {
  /** CSS selector or DOM element for the element to mount the design selector into. */
  divToLoadIn: string | Element;
  scrollContainerEl?: HTMLElement | Window | null;
  loadPostersIfNotLoadedEventType?: string;
  onLoaded?: () => void;
}

export const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: MOBILE_SCREEN_BREAKPOINT,
  },
  large: {
    minWidth: MOBILE_SCREEN_BREAKPOINT + 1,
  },
};

export const skeletonLoaderContainerQuery: ContainerQueryObject = {
  xxsmall: {
    minWidth: 0,
    maxWidth: 499,
  },
  xsmall: {
    minWidth: 500,
    maxWidth: 599,
  },
  small: {
    minWidth: 600,
    maxWidth: 768,
  },
  medium: {
    minWidth: 769,
    maxWidth: 819,
  },
  large: {
    minWidth: 820,
    maxWidth: 899,
  },
  xlarge: {
    minWidth: 900,
    maxWidth: 999,
  },
  xxlarge: {
    minWidth: 1000,
    maxWidth: 1099,
  },
  xxxlarge: {
    minWidth: 1100,
  },
};

/** The designs modal header tabs' key in the shared tabs store. */
export const DESIGN_SELECTOR_PANEL_TABS_ID = 'design-selector-tabs';
